import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getAuthHeader = () => {
  const token = localStorage.getItem("unity_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const HTTP_UNAUTHORIZED = 401;

// Auto-retry transient server failures (deploy rollouts, pod restarts) so users
// never see a hard error when the backend is briefly unavailable.
const RETRYABLE_STATUSES = [502, 503, 504, 520, 521, 522, 524];
const MAX_RETRIES = 3;
axios.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config;
  if (!cfg || error.code === "ERR_CANCELED") return Promise.reject(error);
  const method = (cfg.method || "get").toLowerCase();
  const status = error.response?.status;
  const transient = !error.response || RETRYABLE_STATUSES.includes(status);
  const retryable = method === "get" || (cfg.url || "").includes("/auth/");
  if (retryable && transient) {
    cfg.__retryCount = (cfg.__retryCount || 0) + 1;
    if (cfg.__retryCount <= MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, 1200 * cfg.__retryCount));
      return axios(cfg);
    }
  }
  return Promise.reject(error);
});

export const api = {
  // Auth
  login: (username, password) => axios.post(`${API}/auth/login`, { username, password }),
  verifyAuth: () => axios.get(`${API}/auth/verify`, { headers: getAuthHeader() }),
  refreshToken: () => axios.post(`${API}/auth/refresh`, {}, { headers: getAuthHeader() }),

  // Stats
  getStats: () => axios.get(`${API}/stats`, { headers: getAuthHeader() }),

  // Scan Links
  getScanLinks: () => axios.get(`${API}/scan-links`, { headers: getAuthHeader() }),
  createScanLink: (data) => axios.post(`${API}/scan-links`, data, { headers: getAuthHeader() }),
  deleteScanLink: (id) => axios.delete(`${API}/scan-links/${id}`, { headers: getAuthHeader() }),
  toggleScanLink: (id) => axios.put(`${API}/scan-links/${id}/toggle`, {}, { headers: getAuthHeader() }),

  // Offers
  getOffers: (status, page = 1, limit = 20, clientName = null, search = null) => {
    const params = { status, page, limit };
    if (clientName) params.client_name = clientName;
    if (search) params.search = search;
    return axios.get(`${API}/offers`, { params, headers: getAuthHeader() });
  },
  getOfferClients: () => axios.get(`${API}/offers/clients`, { headers: getAuthHeader() }),
  updateOfferStatus: (id, status) => axios.put(`${API}/offers/${id}/status`, { status }, { headers: getAuthHeader() }),
  deleteOffer: (id) => axios.delete(`${API}/offers/${id}`, { headers: getAuthHeader() }),
  bulkUpdateOffers: (offerIds, status) => axios.post(`${API}/offers/bulk-update`, { offer_ids: offerIds, status }, { headers: getAuthHeader() }),

  // Messages
  getMessages: () => axios.get(`${API}/messages`, { headers: getAuthHeader() }),

  // Settings
  getSettings: () => axios.get(`${API}/settings`, { headers: getAuthHeader() }),
  updateSettings: (data) => axios.put(`${API}/settings`, data, { headers: getAuthHeader() }),

  // Scan
  triggerScan: () => axios.post(`${API}/scan`, {}, { headers: getAuthHeader() }),

  // Filters
  getFilters: () => axios.get(`${API}/filters`, { headers: getAuthHeader() }),
  getDistricts: () => axios.get(`${API}/filters/districts`, { headers: getAuthHeader() }),
  createFilter: (data) => axios.post(`${API}/filters`, data, { headers: getAuthHeader() }),
  previewFilter: (data) => axios.post(`${API}/filters/preview`, data, { headers: getAuthHeader() }),
  getFilteredOffers: (id) => axios.get(`${API}/filters/${id}/offers`, { headers: getAuthHeader() }),
  deleteFilter: (id) => axios.delete(`${API}/filters/${id}`, { headers: getAuthHeader() }),
  toggleFilter: (id) => axios.put(`${API}/filters/${id}/toggle`, {}, { headers: getAuthHeader() }),
  scanFilter: (id) => axios.post(`${API}/filters/${id}/scan`, {}, { headers: getAuthHeader() }),

  // Quick Add to Airtable (async with polling)
  quickAddStart: (url) => axios.post(`${API}/quick-add`, { url }, { headers: getAuthHeader() }),
  quickAddStatus: (taskId) => axios.get(`${API}/quick-add/${taskId}`, { headers: getAuthHeader() }),
  quickAddBatch: (urls) => axios.post(`${API}/quick-add/batch`, { urls }, { headers: getAuthHeader() }),
  quickAddBatchStatus: (batchId) => axios.get(`${API}/quick-add-batch/${batchId}`, { headers: getAuthHeader() }),

  // Legacy webhook (kept for Make.com)
  quickAddToAirtable: (url) => axios.post(`${API}/webhook/create-airtable-record`, { url }, { headers: getAuthHeader(), timeout: 3 * 60 * 1000 }),

  // Telegram test
  testTelegram: () => axios.post(`${API}/settings/test-telegram`, {}, { headers: getAuthHeader() }),
};

export default api;
